// IM-EXT-002 IM-UI-032..038 — Análise de mercado: entendimento, progresso real, resultados, histórico, exportação,
// reexecução, arquivar/reabrir. Fluxo e regras de apresentação transplantados do Ceres (CERES-030..033).
// IM-EXT-009 IM-UI-039..044 — resultado separado em três dimensões: inteligência de MERCADO (aba Mercado), inteligência
// COMPETITIVA (Concorrentes e Comparação) e IMPLICAÇÕES ESTRATÉGICAS (SWOT pela perspectiva da empresa e Estratégia).
// Concorrentes conhecidos (cadastro) separados dos descobertos na análise; arquivados ocultos por padrão.
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { MarketAnalysisDetailDto, MarketRunDto } from '@intelimove/contracts';
import { useAuth } from '../auth/AuthContext';
import { AsyncView, EmptyState } from '../components/States';
import { useToast } from '../components/Toast';
import { PageHead, StatusPill } from '../components/Widgets';
import {
  ACTOR_LABEL, fmtDate, fmtDuration, marketApi, STATUS_LABEL, STATUS_TONE, TYPE_LABEL,
  type CompetitorDecision, type Item, type MarketResult,
} from '../data/market';
import { useResource } from '../hooks/useResource';
import { ArchiveDialog } from './MarketList';
import { Legend, Nature, Sources } from './market/shared';
import { SwotBoard } from './market/SwotBoard';

const STEPS = ['PREPARANDO', 'PROCURANDO_CONCORRENTES', 'ANALISANDO_SITES', 'LEVANTANDO_OFERTAS', 'PROCURANDO_CLIENTES', 'ANALISANDO_PRESENCA', 'MONTANDO_COMPARACAO', 'PREPARANDO_RECOMENDACOES'];
const STEP_TEXT: Record<string, string> = {
  PREPARANDO: 'Preparando e lendo o contexto', PROCURANDO_CONCORRENTES: 'Procurando concorrentes', ANALISANDO_SITES: 'Analisando sites oficiais',
  LEVANTANDO_OFERTAS: 'Levantando ofertas e posicionamento', PROCURANDO_CLIENTES: 'Procurando clientes e evidências', ANALISANDO_PRESENCA: 'Analisando presença digital',
  MONTANDO_COMPARACAO: 'Montando a comparação', PREPARANDO_RECOMENDACOES: 'Preparando achados e recomendações',
};
const TABS: Array<[string, string]> = [['visao', 'Visão geral'], ['mercado', 'Mercado'], ['potencial', 'Mercado potencial'], ['alvos', 'Empresas-alvo'], ['concorrentes', 'Concorrentes'], ['comparacao', 'Comparação'], ['swot', 'SWOT'], ['estrategia', 'Estratégia e recomendações'], ['evidencias', 'Evidências'], ['historico', 'Histórico'], ['escopo', 'Escopo da análise']];
/** Endereços antigos continuam funcionando. */
const TAB_ALIAS: Record<string, string> = { recomendacoes: 'estrategia', entendimento: 'escopo' };
const COVERAGE_TXT: Record<string, string> = { ALTA: 'alta', MEDIA: 'média', BAIXA: 'baixa', SEM_DADOS: 'sem dados' };
const PRIORITY_TXT: Record<string, string> = { ALTA: 'alta', MEDIA: 'média', BAIXA: 'baixa' };

export { Nature };

export function MarketAnalysis() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('aba') ?? 'visao';
  const tab = TAB_ALIAS[rawTab] ?? rawTab;
  const runId = params.get('execucao');
  const toast = useToast();
  const { me } = useAuth();
  const canManage = me?.activeOrganization?.role !== 'MEMBER';
  const [nonce, setNonce] = useState(0);
  const { state, reload } = useResource((s) => marketApi.get(id, s), [id, nonce]);
  const [archiving, setArchiving] = useState(false);
  const refresh = () => { setNonce((n) => n + 1); reload(); };

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast(ok); refresh(); } catch (e) { toast((e as Error).message, 'error'); }
  };
  const go = (aba: string) => { const p = new URLSearchParams(params); p.set('aba', aba); setParams(p, { replace: true }); };

  return (
    <AsyncView state={state} onRetry={reload} empty={null}>
      {(a) => {
        const running = !!a.activeRun;
        return (
          <section aria-labelledby="page-title">
            <PageHead eyebrow={`Inteligência de Mercado · ${TYPE_LABEL[a.analysisType] ?? ''}`} title={a.name} subtitle={a.objective}
              actions={<>
                <StatusPill tone={STATUS_TONE[a.visibleState] ?? 'default'}>{STATUS_LABEL[a.visibleState] ?? a.visibleState}</StatusPill>
                {a.lastRun && <a className="secondary btn-link" href={marketApi.exportUrl(a.id, runId)} download>Exportar XLSX</a>}
                {canManage && !a.archived && a.lastRun && !running && <button className="secondary" onClick={() => act(() => marketApi.start(a.id), 'Nova execução na fila. O resultado anterior continua no histórico.')} disabled={!a.executor.available || !a.entitlement.allowed}>Executar novamente</button>}
                {canManage && (a.archived
                  ? <button className="secondary" onClick={() => act(() => marketApi.reopen(a.id), 'Análise reaberta.')}>Reabrir</button>
                  : <button className="secondary" onClick={() => setArchiving(true)} disabled={running}>Arquivar</button>)}
              </>} />
            <p className="crumbs"><Link to="/mercado">← Todas as análises</Link></p>
            {a.archived && <div className="card notice" role="status"><b>Análise arquivada em {fmtDate(a.archivedAt)}.</b> <span className="card-sub">Resultados e histórico continuam disponíveis. Reabra para executar de novo.</span></div>}
            {a.contextChangedSince && <div className="card notice" role="status"><b>O contexto da empresa mudou desde esta análise</b> (versão {a.contextVersion} → {a.currentContextVersion}). <span className="card-sub">Esta análise continua usando a versão {a.contextVersion}, para que os resultados sejam comparáveis. Para usar o contexto novo, crie uma nova análise.</span></div>}
            {!a.lastRun && !running && <Understanding a={a} canManage={canManage} onStarted={refresh} />}
            {running && <Progress a={a} canManage={canManage} onDone={refresh} />}
            {!running && a.lastRun && ['FAILED', 'CANCELED'].includes(a.lastRun.status) && (
              <div className="card notice danger" role="alert">
                <b>{a.lastRun.status === 'CANCELED' ? 'A última execução foi cancelada.' : 'A última execução não foi concluída.'}</b>
                <p className="card-sub">{a.lastRun.errorSummary ?? 'Sem detalhes.'}</p>
                {canManage && !a.archived && <button className="cta" onClick={() => act(() => marketApi.start(a.id), 'Nova tentativa na fila.')} disabled={!a.executor.available || !a.entitlement.allowed}>Tentar novamente</button>}
                {!a.executor.available && <p className="cell-sub">{a.executor.message}</p>}
              </div>
            )}
            {(a.lastRun || a.runsCount > 0) && (
              <>
                <nav className="tabs" aria-label="Seções da análise">
                  {TABS.filter(([k]) => (k !== 'potencial' || a.analysisType === 'POTENTIAL_MARKET') && (k !== 'alvos' || ['POTENTIAL_MARKET', 'TARGET_DISCOVERY'].includes(a.analysisType))).map(([k, label]) => <a key={k} href={`?aba=${k}`} className={tab === k ? 'active' : ''} aria-current={tab === k ? 'page' : undefined} onClick={(e) => { e.preventDefault(); go(k); }}>{label}</a>)}
                </nav>
                {tab === 'historico' ? <History id={a.id} /> : <ResultTabs a={a} tab={tab} runId={runId} canManage={canManage} go={go} key={`${nonce}-${runId}`} />}
              </>
            )}
            {archiving && <ArchiveDialog name={a.name} onCancel={() => setArchiving(false)} onConfirm={() => { setArchiving(false); void act(() => marketApi.archive(a.id), 'Análise arquivada. Ela continua disponível no filtro “Arquivadas”.'); }} />}
          </section>
        );
      }}
    </AsyncView>
  );
}

function UnderstandingView({ a, scope }: { a: MarketAnalysisDetailDto; scope?: MarketResult['scope'] }) {
  const u = a.understanding;
  return (
    <div className="card">
      <h2 className="card-title">{scope ? 'Escopo da análise' : 'O que será pesquisado'}</h2>
      <p>{u.summary}</p>
      <dl className="kv">
        <dt>Ofertas consideradas</dt><dd>{u.offerings.map((o) => o.name).join(', ')}</dd>
        <dt>Mercado ou região</dt><dd>{u.region} <span className="cell-sub">{u.regionSource}</span></dd>
        <dt>Concorrentes incluídos</dt><dd>{u.knownCompetitors.join(', ') || 'Nenhum informado'}{u.discoverNew ? ' — e outros que a pesquisa descobrir' : ' — somente estes'}</dd>
        <dt>Clientes ideais (ICPs)</dt><dd>{u.icps.join(', ') || '—'}</dd>
        <dt>Personas</dt><dd>{u.personas.join(', ') || '—'}</dd>
        <dt>Assuntos</dt><dd>{u.aspects.map((x) => x.label).join(' · ')}</dd>
        <dt>Concorrentes arquivados</dt><dd>{a.includeArchived ? 'Incluídos a pedido' : 'Não entram na pesquisa'}{scope?.excludedArchived.length ? <span className="cell-sub">Fora desta execução: {scope.excludedArchived.join(', ')}</span> : null}</dd>
      </dl>
      <h3>Perguntas que a análise vai responder</h3>
      <ul className="plain-list">{u.questions.map((q) => <li key={q}>{q}</li>)}</ul>
      <p className="cell-sub">Baseado no contexto confirmado da empresa (versão {u.contextVersion}).</p>
    </div>
  );
}

function Understanding({ a, canManage, onStarted }: { a: MarketAnalysisDetailDto; canManage: boolean; onStarted: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const start = async () => {
    setBusy(true);
    try { await marketApi.start(a.id); toast('Pesquisa iniciada. Você pode acompanhar o progresso aqui.'); onStarted(); } catch (e) { toast((e as Error).message, 'error'); setBusy(false); }
  };
  return (
    <>
      <UnderstandingView a={a} />
      {editing && <EditParams a={a} onSaved={() => { setEditing(false); onStarted(); }} />}
      <div className="card start-card">
        {!a.executor.available && <div className="notice-inline" role="status"><b>Pesquisa por IA indisponível no momento.</b> {a.executor.message} A análise fica salva; nada se perde.</div>}
        {!a.entitlement.allowed && <div className="notice-inline" role="status"><b>Não é possível executar agora.</b> {a.entitlement.reason}</div>}
        <div className="form-actions">
          {canManage && <button type="button" className="secondary" onClick={() => setEditing((e) => !e)} aria-expanded={editing}>Ajustar parâmetros</button>}
          {canManage ? <button className="cta" onClick={start} disabled={busy || !a.executor.available || !a.entitlement.allowed}>{busy ? 'Iniciando…' : 'Está certo — iniciar pesquisa'}</button>
            : <p className="cell-sub">Somente administradores iniciam pesquisas.</p>}
        </div>
      </div>
    </>
  );
}

/** Progresso REAL: o passo vem da última ferramenta usada pelo agente; atualiza a cada 3 s sem recarregar a tela. */
function Progress({ a, canManage, onDone }: { a: MarketAnalysisDetailDto; canManage: boolean; onDone: () => void }) {
  const toast = useToast();
  const [run, setRun] = useState<MarketRunDto>(a.activeRun!);
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const p = await marketApi.progress(a.id);
        if (!p.activeRun) { clearInterval(t); onDone(); return; }
        setRun(p.activeRun);
      } catch { /* tenta de novo no próximo ciclo */ }
    }, 3000);
    return () => clearInterval(t);
  }, [a.id]);
  const idx = run.status === 'QUEUED' ? -1 : STEPS.indexOf(run.step);
  return (
    <div className="card progress-card" aria-live="polite">
      <div className="card-head"><h2 className="card-title">{run.status === 'QUEUED' ? 'Na fila para começar' : run.stepLabel}</h2>
        {canManage && <button className="secondary" disabled={run.cancelRequested} onClick={async () => { try { await marketApi.cancel(a.id); toast('Cancelamento solicitado.'); } catch (e) { toast((e as Error).message, 'error'); } }}>{run.cancelRequested ? 'Cancelando…' : 'Cancelar'}</button>}</div>
      <ol className="steps">{STEPS.map((s, i) => <li key={s} className={i < idx ? 'done' : i === idx ? 'current' : ''}>{STEP_TEXT[s]}</li>)}</ol>
      <p className="card-sub">{run.counts.competitors} concorrente(s) · {run.counts.evidences} fonte(s) registrada(s) · {run.toolCalls} ação(ões) do analista · iniciada {fmtDate(run.startedAt)}{run.attempts > 1 ? ` · tentativa ${run.attempts} de ${run.maxAttempts}` : ''}</p>
      {run.errorSummary && run.status === 'QUEUED' && <p className="cell-sub">{run.errorSummary}</p>}
    </div>
  );
}

function ResultTabs({ a, tab, runId, canManage, go }: { a: MarketAnalysisDetailDto; tab: string; runId: string | null; canManage: boolean; go: (aba: string) => void }) {
  const [params, setParams] = useSearchParams();
  const { state, reload } = useResource((s) => marketApi.result(a.id, runId, s), [a.id, runId], (r) => !r.run);
  return (
    <AsyncView state={state} onRetry={reload} empty={<EmptyState title="Ainda não há resultado">O resultado aparece aqui quando a primeira execução terminar.</EmptyState>}>
      {(r) => (
        <>
          {r.runs.length > 1 && (
            <label className="field run-picker"><span>Execução exibida</span>
              <select value={r.run!.id} onChange={(e) => { const p = new URLSearchParams(params); p.set('execucao', e.target.value); setParams(p, { replace: true }); }}>
                {r.runs.map((x) => <option key={x.id} value={x.id}>#{x.runNo} · {STATUS_LABEL[x.status] ?? x.status} · {fmtDate(x.finishedAt ?? x.createdAt)}</option>)}
              </select></label>
          )}
          {tab === 'visao' && <Overview r={r} go={go} />}
          {tab === 'mercado' && <Market r={r} go={go} />}
          {tab === 'concorrentes' && <Competitors r={r} canManage={canManage} onChanged={reload} />}
          {tab === 'comparacao' && <Comparison r={r} />}
          {tab === 'swot' && (r.swot ? <SwotBoard analysisId={r.analysisId} initial={r.swot} canManage={canManage} go={go} /> : null)}
          {tab === 'estrategia' && <Strategy r={r} canManage={canManage} />}
          {tab === 'evidencias' && <Evidences analysisId={a.id} runId={r.run!.id} r={r} />}
          {tab === 'potencial' && <Segments r={r} />}
          {tab === 'alvos' && <RunTargets r={r} />}
          {tab === 'escopo' && <UnderstandingView a={a} scope={r.scope} />}
        </>
      )}
    </AsyncView>
  );
}


/** "Concluída parcialmente" explicada: o que foi feito, o que faltou, por quê e o que fazer. */
function Completion({ r }: { r: MarketResult }) {
  const c = r.completion;
  if (!c) return null;
  return (
    <div className="card notice" role="status">
      <h2 className="card-title">Concluída parcialmente — o que isso significa</h2>
      <div className="split-2">
        <div><h3>O que foi concluído</h3><ul className="plain-list">{c.done.map((x: string) => <li key={x}>{x}</li>)}</ul></div>
        <div><h3>O que ficou pendente</h3>{c.pending.length ? <ul className="plain-list">{c.pending.map((x: string) => <li key={x}>{x}</li>)}</ul> : <p className="cell-sub">Nenhum item pendente identificado; veja as limitações.</p>}</div>
      </div>
      {c.reasons.length > 0 && <p><b>Motivo:</b> {c.reasons.join('; ')}.</p>}
      {c.limitations && <p className="card-sub"><b>Limitações registradas pelo analista:</b> {c.limitations}</p>}
      <p><b>{c.resumable ? 'Pode ser retomada.' : 'Análise arquivada.'}</b> {c.action}</p>
    </div>
  );
}

function Overview({ r, go }: { r: MarketResult; go: (aba: string) => void }) {
  const run = r.run!;
  const o = r.overview!;
  const cp = o.competitive;
  const mk = o.market;
  const st = o.strategic;
  return (
    <div className="ctx-body">
      <Completion r={r} />
      <div className="overview-3">
        <section className="card" aria-labelledby="ov-comp">
          <h2 className="card-title" id="ov-comp">Panorama competitivo</h2>
          <dl className="kpi-row">
            <div><dt>Confirmados</dt><dd>{cp.confirmed}</dd></div>
            <div><dt>Hipóteses</dt><dd>{cp.hypotheses}</dd></div>
            <div><dt>Descobertos nesta análise</dt><dd>{cp.discovered}</dd></div>
          </dl>
          {(cp.archived > 0 || cp.dismissed > 0) && <p className="cell-sub">{cp.archived > 0 ? `${cp.archived} arquivado(s) ou fora do cadastro` : ''}{cp.archived > 0 && cp.dismissed > 0 ? ' · ' : ''}{cp.dismissed > 0 ? `${cp.dismissed} descartado(s) desta análise` : ''} — ocultos por padrão.</p>}
          <p className="cell-sub">Cobertura da análise: <b>{COVERAGE_TXT[cp.coverage.level]}</b> — {cp.coverage.withDossier} de {cp.coverage.total} concorrente(s) com dossiê e mais de uma fonte.</p>
          {cp.overlaps.length > 0 && <><h3>Principais sobreposições</h3><ul className="plain-list">{cp.overlaps.map((x: any) => <li key={x.id}><b>{x.name}</b> <span className="inline-sub">({GROUP_SHORT[x.group]})</span> — {x.text}</li>)}</ul></>}
          {cp.movements.length > 0 && <><h3>Movimentos relevantes</h3><ul className="plain-list">{cp.movements.map((f: Item) => <li key={f.id}>{f.title} <Nature n={f.nature} /></li>)}</ul></>}
          <button className="link-btn" onClick={() => go('concorrentes')}>Ver concorrentes</button>
        </section>
        <section className="card" aria-labelledby="ov-mkt">
          <h2 className="card-title" id="ov-mkt">Oportunidades de mercado</h2>
          {mk.forWhom && <p><b>Para quem vender:</b> {mk.forWhom}</p>}
          {mk.segments.length > 0 && <p><b>Segmentos:</b> {mk.segments.join(', ')}</p>}
          {mk.opportunities.length > 0 && <><h3>Oportunidades</h3><ul className="plain-list">{mk.opportunities.map((f: Item) => <li key={f.id}>{f.title} <Nature n={f.nature} /></li>)}</ul></>}
          {mk.trends.length > 0 && <><h3>Tendências</h3><ul className="plain-list">{mk.trends.map((f: Item) => <li key={f.id}>{f.title} <Nature n={f.nature} /></li>)}</ul></>}
          {mk.gaps.length > 0 && <><h3>Lacunas</h3><ul className="plain-list">{mk.gaps.map((f: Item) => <li key={f.id}>{f.title}</li>)}</ul></>}
          {!mk.forWhom && !mk.opportunities.length && !mk.trends.length && <p className="cell-sub">Esta execução não registrou oportunidades de mercado.</p>}
          <button className="link-btn" onClick={() => go('mercado')}>Ver mercado</button>
        </section>
        <section className="card" aria-labelledby="ov-str">
          <h2 className="card-title" id="ov-str">Implicações estratégicas</h2>
          {st.priorities.length > 0 && <><h3>Recomendações prioritárias</h3><ul className="plain-list">{st.priorities.map((x: any) => <li key={x.id}>{x.title} <span className="cell-sub">{x.origins.map((o: any) => o.label).join(' · ')}</span></li>)}</ul></>}
          {st.differentiation.length > 0 && <><h3>Diferenciação possível</h3><ul className="plain-list">{st.differentiation.map((x: any) => <li key={x.id}>{x.title} <span className="cell-sub">{x.perspectiveLabel}</span></li>)}</ul></>}
          {st.risks.length > 0 && <><h3>Riscos</h3><ul className="plain-list">{st.risks.map((x: any) => <li key={x.id}>{x.title} <span className="cell-sub">{x.perspectiveLabel}</span></li>)}</ul></>}
          {st.toValidate.length > 0 && <><h3>Depende de validação</h3><ul className="plain-list">{st.toValidate.map((x: string) => <li key={x}>{x}</li>)}</ul></>}
          <button className="link-btn" onClick={() => go('estrategia')}>Ver estratégia e recomendações</button>
        </section>
      </div>
      <details className="card details">
        <summary>Resumo escrito pelo analista</summary>
        <p>{o.summary ?? 'A execução não registrou um resumo.'}</p>
        <p className="cell-sub">{run.counts.competitors} concorrente(s) · {run.counts.evidences} fonte(s) · {run.counts.findings} achado(s) · {run.counts.recommendations} recomendação(ões) · execução #{run.runNo} em {fmtDate(run.finishedAt)} · contexto v{run.contextVersion}</p>
      </details>
      <Legend />
    </div>
  );
}

const SOURCE_TAG = <span className="tag">Do contexto da empresa</span>;
const FindingList = ({ r, items, empty }: { r: MarketResult; items: Item[]; empty: string }) => (
  items.length ? <ul className="finding-list">{items.map((f) => (
    <li key={f.id}><div><b>{f.title}</b> <Nature n={f.nature} />{f.nature === 'HIPOTESE' && <span className="tag">Inferência da análise</span>}</div><p>{f.description}</p><Sources analysisId={r.analysisId} runId={r.run!.id} ids={f.evidenceIds} /></li>
  ))}</ul> : <p className="cell-sub">{empty}</p>
);

/** Inteligência de MERCADO: para quem vender, com quem conversar, por que e quando; ICPs, personas, tendências, riscos. */
function Market({ r, go }: { r: MarketResult; go: (aba: string) => void }) {
  const m = r.market;
  if (!m) return null;
  const run = r.run!;
  return (
    <div className="ctx-body">
      <div className="card">
        <h2 className="card-title">Síntese comercial</h2>
        <p className="card-sub">Respostas da análise sobre o mercado — não são perfil de concorrente. Inferências aparecem marcadas como hipótese.</p>
        <div className="synthesis">
          {m.synthesis.map((s: any) => (
            <article key={s.key}><h3>{s.question}</h3>{s.text ? <><p>{s.text}</p><Nature n={s.nature} />{s.nature === 'HIPOTESE' && <span className="tag">Inferência da análise</span>} <Sources analysisId={r.analysisId} runId={run.id} ids={s.evidenceIds} /></> : <p className="cell-sub">Não respondido nesta execução.</p>}</article>
          ))}
        </div>
      </div>
      <div className="split-2">
        <div className="card">
          <h2 className="card-title">Clientes ideais (ICPs) {SOURCE_TAG}</h2>
          {m.icps.length ? <ul className="finding-list">{m.icps.map((i: any) => (
            <li key={i.name}><b>{i.name}</b>
              {i.segments.length > 0 && <p><b>Segmentos:</b> {i.segments.join(', ')}</p>}
              {i.locations.length > 0 && <p><b>Regiões:</b> {i.locations.join(', ')}</p>}
              {i.pains.length > 0 && <p><b>Dores:</b> {i.pains.join('; ')}</p>}
              {i.needs.length > 0 && <p><b>Necessidades:</b> {i.needs.join('; ')}</p>}
              {i.triggers.length > 0 && <p><b>Gatilhos de compra:</b> {i.triggers.join('; ')}</p>}
            </li>))}</ul> : <p className="cell-sub">Nenhum ICP confirmado na versão do contexto usada por esta análise.</p>}
        </div>
        <div className="card">
          <h2 className="card-title">Personas {SOURCE_TAG}</h2>
          {m.personas.length ? <ul className="finding-list">{m.personas.map((p: any) => (
            <li key={p.name}><b>{p.name}</b>{p.titles.length > 0 && <span className="cell-sub"> · {p.titles.join(', ')}</span>}
              {p.pains.length > 0 && <p><b>Dores:</b> {p.pains.join('; ')}</p>}
              {p.goals.length > 0 && <p><b>Objetivos:</b> {p.goals.join('; ')}</p>}
            </li>))}</ul> : <p className="cell-sub">Nenhuma persona confirmada na versão do contexto usada por esta análise.</p>}
          <p className="cell-sub"><b>Região analisada:</b> {m.region ?? '—'}{m.regionSource ? ` (${m.regionSource.toLowerCase()})` : ''}</p>
        </div>
      </div>
      <div className="card"><h2 className="card-title">Oportunidades</h2><FindingList r={r} items={m.opportunities} empty="Nenhuma oportunidade de mercado registrada nesta execução." /></div>
      <div className="card"><h2 className="card-title">Tendências e sinais</h2><FindingList r={r} items={m.trends} empty="Nenhuma tendência registrada nesta execução." /></div>
      <div className="card"><h2 className="card-title">Riscos externos</h2><FindingList r={r} items={m.risks} empty="Nenhum risco externo (fora de concorrentes) registrado. Ameaças ligadas a concorrentes estão na SWOT." /></div>
      <div className="card"><h2 className="card-title">Lacunas e pontos não verificados</h2><FindingList r={r} items={m.gaps} empty="Nenhuma lacuna registrada nesta execução." /></div>
      {r.segments && r.segments.length > 0 && <p><button className="link-btn" onClick={() => go('potencial')}>Ver segmentos do mercado potencial ({r.segments.length})</button></p>}
      <Legend />
    </div>
  );
}

const GROUP_SHORT: Record<string, string> = { CONFIRMED: 'confirmado', HYPOTHESIS: 'hipótese', DISCOVERED: 'descoberto nesta análise', DISMISSED: 'descartado desta análise', ARCHIVED: 'arquivado' };
const GROUP_TONE: Record<string, string> = { CONFIRMED: 'mint', HYPOTHESIS: 'amber', DISCOVERED: 'petrol', DISMISSED: 'gray', ARCHIVED: 'gray' };
const profileLink = (id: string) => `/configuracoes/contexto/concorrentes?c=${id}`;

/** Inteligência COMPETITIVA: conhecidos (confirmados e hipóteses) × descobertos nesta análise × arquivados (ocultos). */
function Competitors({ r, canManage, onChanged }: { r: MarketResult; canManage: boolean; onChanged: () => void }) {
  const [showArchived, setShowArchived] = useState(false);
  const cs = r.competitors ?? [];
  if (!cs.length) return <EmptyState title="Nenhum concorrente nesta execução">A pesquisa não registrou concorrentes. Veja as limitações na visão geral.</EmptyState>;
  const of = (g: string) => cs.filter((c: any) => c.group === g);
  const card = (c: any) => <CompetitorCard key={c.id} c={c} r={r} canManage={canManage} onChanged={onChanged} />;
  const hidden = of('ARCHIVED').length;
  return (
    <div className="ctx-body">
      <section aria-labelledby="g-known">
        <h2 id="g-known" className="section-title">Concorrentes conhecidos</h2>
        <p className="card-sub">Empresas que estão no cadastro de concorrentes do Contexto da empresa. A situação mostrada é a atual do cadastro.</p>
        <h3>Confirmados ({of('CONFIRMED').length})</h3>
        {of('CONFIRMED').length ? <div className="market-grid">{of('CONFIRMED').map(card)}</div> : <p className="cell-sub">Nenhum concorrente confirmado nesta execução.</p>}
        <h3>Hipóteses aguardando revisão ({of('HYPOTHESIS').length})</h3>
        {of('HYPOTHESIS').length ? <div className="market-grid">{of('HYPOTHESIS').map(card)}</div> : <p className="cell-sub">Nenhuma hipótese nesta execução.</p>}
      </section>
      <section aria-labelledby="g-disc">
        <h2 id="g-disc" className="section-title">Descobertos nesta análise ({of('DISCOVERED').length})</h2>
        <p className="card-sub">Empresas encontradas pela pesquisa que ainda não fazem parte dos concorrentes conhecidos. Não são concorrentes confirmados: decida se entram no contexto.</p>
        {of('DISCOVERED').length ? <div className="market-grid">{of('DISCOVERED').map(card)}</div> : <p className="cell-sub">A pesquisa não descobriu empresas novas nesta execução.</p>}
      </section>
      {of('DISMISSED').length > 0 && (
        <details className="details"><summary>Descartados desta análise ({of('DISMISSED').length})</summary><div className="market-grid">{of('DISMISSED').map(card)}</div></details>
      )}
      {hidden > 0 && (
        <section aria-labelledby="g-arch">
          <label className="check"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Mostrar arquivados ({hidden})</label>
          {showArchived && <><h2 id="g-arch" className="section-title">Arquivados</h2><p className="card-sub">Arquivados ou descartados no cadastro (depois ou antes desta execução), ou presentes só nesta versão histórica. Não entram na comparação.</p><div className="market-grid">{of('ARCHIVED').map(card)}</div></>}
        </section>
      )}
      <Legend />
    </div>
  );
}

function CompetitorCard({ c, r, canManage, onChanged }: { c: any; r: MarketResult; canManage: boolean; onChanged: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const decide = async (d: CompetitorDecision) => {
    let note: string | undefined;
    if (d === 'DISMISS') {
      const v = window.prompt(`Por que descartar "${c.name}" desta análise? (opcional — as evidências são mantidas)`);
      if (v === null) return;
      note = v.trim() || undefined;
    }
    setBusy(true);
    try {
      const x = await marketApi.decideCompetitor(r.analysisId, c.id, d, note);
      toast(x.promotion?.message ?? (d === 'DISMISS' ? 'Descartada desta análise. Evidências e histórico foram mantidos.' : d === 'INVESTIGATE' ? 'Marcada para investigar.' : 'Decisão desfeita.'));
      onChanged();
    } catch (e) { toast((e as Error).message, 'error'); } finally { setBusy(false); setAdding(false); }
  };
  const discovered = c.group === 'DISCOVERED';
  return (
    <article className="card competitor-card">
      <div className="signal-card-top">
        <StatusPill tone={GROUP_TONE[c.group] ?? 'gray'}>{c.statusLabel}</StatusPill>
        <span className="tag">{ACTOR_LABEL[c.actorType] ?? c.actorType}</span>
        {c.evidenceInsufficient && <span className="tag amber">Evidência insuficiente</span>}
        {c.laterArchived && <span className="tag">Arquivado posteriormente</span>}
      </div>
      <h3>{c.name}</h3>
      {c.website && <a className="cell-sub" href={c.website} target="_blank" rel="noopener noreferrer nofollow">{c.domain ?? c.website}</a>}
      <p className="cell-sub"><b>Origem:</b> {c.originLabel}</p>
      {c.rationale && <p><b>Por que pode ser concorrente:</b> {c.rationale}</p>}
      {c.offerings.length > 0 && <p><b>Ofertas relacionadas:</b> {c.offerings.map((o: any) => o.name).join(', ')}</p>}
      {(c.coverage || c.location) && <p><b>Onde atua:</b> {c.coverage ?? c.location}</p>}
      <p className="cell-sub">Confiança {c.confidence}% · {c.confirmedAt ? `confirmado em ${fmtDate(c.confirmedAt)}` : `encontrado em ${fmtDate(c.discoveredAt)}`}{c.discoveryNote ? ` · nota: ${c.discoveryNote}` : ''}</p>
      <Sources analysisId={r.analysisId} runId={r.run!.id} ids={c.evidenceIds} />
      <details className="details"><summary>Perfil nesta análise</summary>
        {c.positioning && <p><b>Posicionamento:</b> {c.positioning}</p>}
        {c.strengths.length > 0 && <p><b>Forças do concorrente:</b> {c.strengths.join('; ')}</p>}
        {c.weaknesses.length > 0 && <p><b>Fragilidades do concorrente:</b> {c.weaknesses.join('; ')}</p>}
        {c.offerings.length > 0 && <><h4>Ofertas ({c.offerings.length})</h4><ul>{c.offerings.map((o: any) => <li key={o.id}><b>{o.name}</b> <Nature n={o.nature} />{o.category && <span className="cell-sub"> · {o.category}</span>}{o.commercialModel && <span className="cell-sub"> · {o.commercialModel}</span>}{o.audience && <span className="cell-sub"> · para {o.audience}</span>}</li>)}</ul></>}
        {c.clients.length > 0 && <><h4>Clientes e cases públicos ({c.clients.length})</h4><ul>{c.clients.map((x: any) => <li key={x.id}>{x.name} <Nature n={x.nature} /> <span className="cell-sub">{x.evidenceType}{x.segment ? ` · ${x.segment}` : ''}</span></li>)}</ul></>}
        {c.channels.length > 0 && <><h4>Presença digital ({c.channels.length})</h4><ul>{c.channels.map((x: any) => <li key={x.id}><a href={x.url} target="_blank" rel="noopener noreferrer nofollow">{x.label}</a>{x.cadence && <span className="cell-sub"> · {x.cadence}</span>}</li>)}</ul></>}
        {!c.positioning && !c.offerings.length && !c.strengths.length && <p className="cell-sub">Não identificado nas fontes consultadas.</p>}
      </details>
      <div className="row-actions" role="group" aria-label={`Ações sobre ${c.name}`}>
        {c.canonicalId && <Link className="chip" to={profileLink(c.canonicalId)}>Abrir perfil</Link>}
        {canManage && discovered && !adding && <button type="button" className="chip" disabled={busy} onClick={() => setAdding(true)}>Adicionar aos concorrentes conhecidos</button>}
        {canManage && discovered && adding && <>
          <button type="button" className="chip" disabled={busy} onClick={() => decide('PROMOTE_CONFIRMED')}>Como confirmado</button>
          <button type="button" className="chip" disabled={busy} onClick={() => decide('PROMOTE_HYPOTHESIS')}>Como hipótese</button>
          <button type="button" className="link-btn" onClick={() => setAdding(false)}>Cancelar</button></>}
        {canManage && discovered && c.discoveryStatus !== 'INVESTIGATING' && !adding && <button type="button" className="chip" disabled={busy} onClick={() => decide('INVESTIGATE')}>Investigar</button>}
        {canManage && discovered && !adding && <button type="button" className="chip" disabled={busy} onClick={() => decide('DISMISS')}>Descartar desta análise</button>}
        {canManage && (c.group === 'DISMISSED' || (discovered && c.discoveryStatus === 'INVESTIGATING')) && <button type="button" className="chip" disabled={busy} onClick={() => decide('RESET')}>Desfazer</button>}
      </div>
    </article>
  );
}

/** Comparação de 2 a 4 concorrentes. Cabeçalho fixo; telas estreitas: cartões por critério. */
function Comparison({ r }: { r: MarketResult }) {
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [onlyEv, setOnlyEv] = useState(false);
  const [showOut, setShowOut] = useState(false);
  const cmp = r.comparison;
  const options = (cmp?.companies ?? []).filter((e) => !e.reference);
  const [sel, setSel] = useState<string[]>(() => options.filter((e) => e.defaultSelected).slice(0, 4).map((e) => e.key));
  if (!cmp?.groups.length) return <EmptyState title="Comparação indisponível">Ainda não há informação suficiente para comparar nesta execução.</EmptyState>;
  const keep = (c: any) => (!onlyDiff || c.hasDifference) && (!onlyEv || c.withEvidence);
  const toggle = (k: string) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : s.length >= 4 ? s : [...s, k]));
  const cols = [0, ...cmp.companies.map((e, i) => (sel.includes(e.key) ? i : -1)).filter((i) => i > 0)];
  const visibleOptions = options.filter((e) => showOut || !['DISMISSED', 'ARCHIVED'].includes(e.group ?? '') || sel.includes(e.key));
  const head = (i: number) => { const e = cmp.companies[i]!; return <>{e.name}<span className="cell-sub">{e.reference ? 'sua empresa' : e.statusLabel}</span></>; };
  const cellView = (x: any) => <>{x.summary}{x.nature && x.identified && <> <Nature n={x.nature} /></>}{x.evidenceIds?.length > 0 && <Sources analysisId={r.analysisId} runId={r.run!.id} ids={x.evidenceIds} />}</>;
  return (
    <div className="ctx-body">
      <fieldset className="card cmp-picker"><legend>Escolha de 2 a 4 empresas para comparar com a sua</legend>
        <div className="check-grid">{visibleOptions.map((e) => (
          <label key={e.key} className="check"><input type="checkbox" checked={sel.includes(e.key)} disabled={!sel.includes(e.key) && sel.length >= 4} onChange={() => toggle(e.key)} /> {e.name} <span className={`tag ${GROUP_TONE[e.group ?? ''] ?? ''}`}>{GROUP_SHORT[e.group ?? ''] ?? ''}</span></label>
        ))}</div>
        <p className="cell-sub">{sel.length < 2 ? 'Selecione pelo menos duas empresas.' : `${sel.length} selecionada(s).`} Descobertas ainda não incorporadas aparecem marcadas; descartadas e arquivadas não entram por padrão.</p>
        <div className="filters plain" role="group" aria-label="Filtrar comparação">
          <label className="check"><input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} /> Somente diferenças</label>
          <label className="check"><input type="checkbox" checked={onlyEv} onChange={(e) => setOnlyEv(e.target.checked)} /> Somente com fonte</label>
          <label className="check"><input type="checkbox" checked={showOut} onChange={(e) => setShowOut(e.target.checked)} /> Mostrar descartados e arquivados na escolha</label>
        </div>
      </fieldset>
      <p className="legend"><Nature n="FATO" /> fato · <Nature n="INDICIO" /> indício · <Nature n="HIPOTESE" /> hipótese · <i>“Não identificado nas fontes consultadas”</i> significa que não foi encontrado — nunca que a empresa não possui.</p>
      {sel.length >= 2 && cmp.groups.map((g) => {
        const crit = g.criteria.filter(keep);
        if (!crit.length) return null;
        return (
          <div className="card" key={g.key}>
            <h2 className="card-title">{g.label}</h2>
            <div className="cmp-table table-scroll sticky-head">
              <table className="data-table">
                <thead><tr><th scope="col">Critério</th>{cols.map((i) => <th key={i} scope="col">{head(i)}</th>)}</tr></thead>
                <tbody>{crit.map((c: any) => (
                  <tr key={c.key}><th scope="row">{c.label}</th>{cols.map((i) => { const x = c.cells[i]; return <td key={i} className={x.identified ? '' : 'muted'} title={x.full || undefined}>{cellView(x)}</td>; })}</tr>
                ))}</tbody>
              </table>
            </div>
            <div className="cmp-cards">{crit.map((c: any) => (
              <section key={c.key} className="cmp-card"><h3>{c.label}</h3>
                <dl>{cols.map((i) => { const x = c.cells[i]; const e = cmp.companies[i]!; return <div key={i}><dt>{e.name}{e.reference ? ' (sua empresa)' : ` · ${e.statusLabel ?? ''}`}</dt><dd className={x.identified ? '' : 'muted'}>{x.full || x.summary}{x.nature && x.identified && <> <Nature n={x.nature} /></>}{x.evidenceIds?.length > 0 && <Sources analysisId={r.analysisId} runId={r.run!.id} ids={x.evidenceIds} />}</dd></div>; })}</dl>
              </section>
            ))}</div>
          </div>
        );
      })}
    </div>
  );
}


/** Estratégia: toda recomendação mostra de onde vem (mercado, movimento competitivo, fragilidade, ameaça, sinal, lacuna). */
function Strategy({ r, canManage }: { r: MarketResult; canManage: boolean }) {
  const toast = useToast();
  const [decided, setDecided] = useState<Record<string, string>>({});
  if (!r.recommendations?.length) return <EmptyState title="Nenhuma recomendação nesta execução">Veja a SWOT e o mercado para os achados desta execução.</EmptyState>;
  const decide = async (id: string, d: 'ACEITA' | 'DESCARTADA' | 'PENDENTE') => {
    try { const x = await marketApi.decideRecommendation(r.analysisId, id, d); setDecided((m) => ({ ...m, [id]: x.decision })); } catch (e) { toast((e as Error).message, 'error'); }
  };
  const toValidate: string[] = r.overview?.strategic?.toValidate ?? [];
  return (
    <div className="ctx-body">
      {r.recommendations.map((x) => {
        const st = decided[x.id] ?? x.decision;
        return (
          <article className="card" key={x.id}>
            <div className="signal-card-top">
              <span className="tag">Prioridade {PRIORITY_TXT[x.priority] ?? x.priority}</span>
              {x.origins.map((o: any) => <span key={o.key} className={`tag ${o.key === 'HIPOTESE' ? 'amber' : 'petrol'}`}>{o.label}</span>)}
              {x.hypothesis ? <Nature n="HIPOTESE" /> : null}
              <StatusPill tone={st === 'ACEITA' ? 'mint' : st === 'DESCARTADA' ? 'gray' : 'amber'}>{st === 'ACEITA' ? 'Aceita' : st === 'DESCARTADA' ? 'Descartada' : 'A decidir'}</StatusPill>
            </div>
            <h3>{x.title}</h3>
            <p><b>Recomendação:</b> {x.action}</p>
            <p><b>Justificativa:</b> {x.rationale}</p>
            {x.basedOn.length > 0 && <p className="cell-sub"><b>Com base em:</b> {x.basedOn.map((f: any) => `${f.title}${f.competitor ? ` (${f.competitor})` : ''}`).join('; ')}</p>}
            <p className="cell-sub">Confiança {x.confidence}%{x.impact ? ` · impacto: ${x.impact}` : ''}{x.horizon ? ` · horizonte: ${x.horizon.toLowerCase()}` : ''}</p>
            {x.dependencies && <p className="cell-sub"><b>Depende de:</b> {x.dependencies}</p>}
            <Sources analysisId={r.analysisId} runId={r.run!.id} ids={x.supportEvidenceIds} />
            {canManage && <div className="row-actions">{st !== 'ACEITA' && <button className="chip" onClick={() => decide(x.id, 'ACEITA')}>Aceitar</button>}{st !== 'DESCARTADA' && <button className="chip" onClick={() => decide(x.id, 'DESCARTADA')}>Descartar</button>}{st !== 'PENDENTE' && <button className="chip" onClick={() => decide(x.id, 'PENDENTE')}>Desfazer</button>}</div>}
          </article>
        );
      })}
      {toValidate.length > 0 && <div className="card"><h2 className="card-title">Pontos que dependem de validação</h2><ul className="plain-list">{toValidate.map((t) => <li key={t}>{t}</li>)}</ul></div>}
    </div>
  );
}

const CONF_TXT: Record<string, string> = { ALTA: 'Alta', MEDIA: 'Média', BAIXA: 'Baixa' };
function Evidences({ analysisId, runId, r }: { analysisId: string; runId: string; r: MarketResult }) {
  const [items, setItems] = useState<any[] | null>(null);
  const [f, setF] = useState({ company: '', section: '', nature: '', confidence: '', domain: '', since: '' });
  useEffect(() => {
    let alive = true;
    (async () => {
      const all: any[] = [];
      let cursor: string | null = null;
      do {
        const p: { items: any[]; nextCursor: string | null } = await marketApi.evidences(analysisId, { runId, cursor, limit: 200 }).catch(() => ({ items: [], nextCursor: null }));
        all.push(...p.items);
        cursor = p.nextCursor;
      } while (cursor && all.length < 2000);
      if (alive) setItems(all);
    })();
    return () => { alive = false; };
  }, [analysisId, runId]);
  if (items === null) return <div className="card"><p className="cell-sub">Carregando fontes…</p></div>;
  if (!items.length) return <EmptyState title="Nenhuma fonte registrada">Esta execução não registrou evidências.</EmptyState>;
  const uniq = (v: Array<string | null | undefined>) => [...new Set(v.filter((x): x is string => !!x))].sort((a, b) => a.localeCompare(b));
  const companies = uniq(items.map((e) => e.competitor));
  const sections = uniq(items.flatMap((e) => e.sections ?? []));
  const domains = uniq(items.map((e) => e.domain));
  const shown = items.filter((e) => (!f.company || (f.company === '—' ? !e.competitor : e.competitor === f.company)) && (!f.section || (e.sections ?? []).includes(f.section))
    && (!f.nature || e.nature === f.nature) && (!f.confidence || e.confidence === f.confidence) && (!f.domain || e.domain === f.domain)
    && (!f.since || (e.collectedAt && e.collectedAt.slice(0, 10) >= f.since)));
  const set = (k: keyof typeof f) => (ev: { target: { value: string } }) => setF((x) => ({ ...x, [k]: ev.target.value }));
  return (
    <div className="card">
      <h2 className="card-title">Fontes consultadas</h2>
      <p className="card-sub">Execução #{r.run!.runNo} desta análise. Informação pública não é verdade absoluta: cada fonte traz a natureza do que sustenta e o trecho citado.</p>
      <div className="filters evidence-filters" role="group" aria-label="Filtrar evidências">
        <label className="field"><span>Empresa</span><select value={f.company} onChange={set('company')}><option value="">Todas</option><option value="—">Sem empresa (mercado)</option>{companies.map((x) => <option key={x}>{x}</option>)}</select></label>
        <label className="field"><span>Seção</span><select value={f.section} onChange={set('section')}><option value="">Todas</option>{sections.map((x) => <option key={x}>{x}</option>)}</select></label>
        <label className="field"><span>Natureza</span><select value={f.nature} onChange={set('nature')}><option value="">Todas</option><option value="FATO">Fato</option><option value="INDICIO">Indício</option><option value="HIPOTESE">Hipótese</option></select></label>
        <label className="field"><span>Confiança</span><select value={f.confidence} onChange={set('confidence')}><option value="">Todas</option><option value="ALTA">Alta</option><option value="MEDIA">Média</option><option value="BAIXA">Baixa</option></select></label>
        <label className="field"><span>Fonte</span><select value={f.domain} onChange={set('domain')}><option value="">Todas</option>{domains.map((x) => <option key={x}>{x}</option>)}</select></label>
        <label className="field"><span>Coletada desde</span><input type="date" value={f.since} onChange={set('since')} /></label>
      </div>
      <p className="cell-sub">{shown.length} de {items.length} fonte(s).</p>
      <ul className="evidence-list">{shown.map((e) => (
        <li key={e.id}><div><Nature n={e.nature} /> <a href={e.url ?? undefined} target="_blank" rel="noopener noreferrer nofollow">{e.title}</a>{e.competitor && <span className="tag">{e.competitor}</span>}{(e.sections ?? []).map((s: string) => <span key={s} className="tag">{s}</span>)}</div>
          {e.excerpt && <q>{e.excerpt}</q>}
          <span className="cell-sub">{e.domain ?? ''}{e.url ? <> · <span className="url-text">{e.url}</span></> : null}{e.publishedAt ? ` · publicada ${fmtDate(e.publishedAt)}` : ''} · coletada {fmtDate(e.collectedAt)}{e.confidence ? ` · confiança ${(CONF_TXT[e.confidence] ?? String(e.confidence)).toLowerCase()}` : ''}</span></li>
      ))}</ul>
    </div>
  );
}

const EVENT_LABEL: Record<string, string> = {
  'market_analysis.created': 'Análise criada', 'market_analysis.updated': 'Parâmetros alterados', 'market_analysis.started': 'Pesquisa iniciada',
  'market_analysis.rerun': 'Nova execução solicitada', 'market_analysis.run.started': 'Execução começou', 'market_analysis.run.completed': 'Execução concluída',
  'market_analysis.run.partial': 'Execução concluída parcialmente', 'market_analysis.run.failed': 'Execução não concluída', 'market_analysis.run.canceled': 'Execução cancelada',
  'market_analysis.cancel_requested': 'Cancelamento solicitado', 'market_analysis.archived': 'Análise arquivada', 'market_analysis.reopened': 'Análise reaberta',
  'market_analysis.exported': 'Exportação gerada', 'market_analysis.competitor_decided': 'Decisão sobre empresa descoberta', 'market_analysis.recommendation_decided': 'Decisão sobre recomendação',
  'market_analysis.competitor_promoted': 'Empresa adicionada aos concorrentes conhecidos',
};

function History({ id }: { id: string }) {
  const { state, reload } = useResource(() => marketApi.history(id), [id]);
  return (
    <AsyncView state={state} onRetry={reload} empty={null}>
      {(h) => (
        <div className="ctx-body">
          <div className="card"><h2 className="card-title">Execuções</h2>
            <div className="table-scroll"><table className="data-table">
              <thead><tr><th>#</th><th>Situação</th><th>Início</th><th>Duração</th><th>Contexto</th><th>Resultado</th><th>Quem pediu</th></tr></thead>
              <tbody>{h.runs.map((r) => (
                <tr key={r.id}><td><Link to={`?aba=visao&execucao=${r.id}`}>#{r.runNo}</Link>{r.trigger === 'RERUN' ? <span className="cell-sub">reexecução</span> : null}</td>
                  <td><StatusPill tone={STATUS_TONE[r.status] ?? 'default'}>{STATUS_LABEL[r.status] ?? r.status}</StatusPill>{r.errorSummary && <span className="cell-sub">{r.errorSummary}</span>}</td>
                  <td>{fmtDate(r.startedAt ?? r.createdAt)}</td><td>{fmtDuration(r.durationMs)}{r.attempts > 1 ? <span className="cell-sub">{r.attempts} tentativas</span> : null}</td>
                  <td>v{r.contextVersion}<span className="cell-sub">estratégia {r.strategyVersion}</span></td>
                  <td>{r.counts.competitors} concorrente(s), {r.counts.evidences} fonte(s)<span className="cell-sub">{r.toolCalls} ação(ões) do analista</span></td>
                  <td>{r.requestedBy ?? '—'}</td></tr>
              ))}</tbody>
            </table></div>
          </div>
          <div className="card"><h2 className="card-title">Linha do tempo</h2>
            <ul className="plain-list">{h.events.map((e, i) => <li key={i}><b>{EVENT_LABEL[e.action] ?? e.action}</b> <span className="cell-sub">{fmtDate(e.at)}{e.actor ? ` · ${e.actor}` : ''}</span></li>)}</ul>
          </div>
        </div>
      )}
    </AsyncView>
  );
}

/** Parâmetros permitidos (sem execução viva). A identidade da empresa e o snapshot não são editáveis aqui. */
function EditParams({ a, onSaved }: { a: MarketAnalysisDetailDto; onSaved: () => void }) {
  const toast = useToast();
  const [v, setV] = useState({ name: a.name, objective: a.objective, region: a.region ?? '', notes: a.notes ?? '', discoverNew: a.discoverNew });
  const save = async () => {
    try {
      await marketApi.update(a.id, { name: v.name, objective: v.objective, region: v.region.trim() || null, notes: v.notes.trim() || null, discoverNew: v.discoverNew });
      toast('Parâmetros atualizados. Confira o entendimento antes de iniciar.');
      onSaved();
    } catch (e) { toast((e as Error).message, 'error'); }
  };
  return (
    <div className="card"><div className="form-grid">
      <label className="field"><span>Nome</span><input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} /></label>
      <label className="field"><span>Região ou mercado</span><input value={v.region} onChange={(e) => setV({ ...v, region: e.target.value })} /></label>
      <label className="field wide"><span>Objetivo comercial</span><textarea rows={3} value={v.objective} onChange={(e) => setV({ ...v, objective: e.target.value })} /></label>
      <label className="field wide"><span>Observações</span><textarea rows={2} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} /></label>
      <label className="field check-inline"><input type="checkbox" checked={v.discoverNew} onChange={(e) => setV({ ...v, discoverNew: e.target.checked })} /> Descobrir concorrentes que ainda não conheço</label>
    </div><div className="form-actions"><button className="cta" onClick={save}>Salvar parâmetros</button></div></div>
  );
}

const FIT_TXT: Record<string, string> = { ALTA: 'Alta', MEDIA: 'Média', BAIXA: 'Baixa', INDETERMINADA: 'Indeterminada' };
const SEG_KIND: Record<string, string> = { SEGMENTO: 'Segmento', REGIAO: 'Região', NICHO: 'Nicho' };

/** IM-EXT-003: mercado potencial — segmento, região e nicho; tamanho só com fonte/metodologia ou como hipótese. */
function Segments({ r }: { r: MarketResult }) {
  if (!r.segments?.length) return <EmptyState title="Nenhum segmento nesta execução">O mercado potencial aparece aqui quando a pesquisa registrar segmentos, regiões ou nichos.</EmptyState>;
  return (
    <div className="market-grid">{r.segments.map((s) => (
      <article key={s.id} className="card market-card">
        <div className="signal-card-top"><span className="tag mint">{SEG_KIND[s.kind]}</span><StatusPill tone={s.fitLevel === 'ALTA' ? 'mint' : s.fitLevel === 'MEDIA' ? 'amber' : 'gray'}>Aderência {FIT_TXT[s.fitLevel]}</StatusPill></div>
        <h3>{s.name} <Nature n={s.nature} /></h3>
        {s.rationale && <p className="card-sub">{s.rationale}</p>}
        {s.searchTerms?.length > 0 && <p className="cell-sub">Termos de busca: {s.searchTerms.join(' · ')}</p>}
        {s.sizeClaim && <p className="cell-sub"><b>Tamanho:</b> {s.sizeClaim} — {s.sizeMethod ?? (s.evidenceIds.length ? 'com fonte citada' : 'hipótese, sem fonte')}</p>}
        <Sources analysisId={r.analysisId} runId={r.run!.id} ids={s.evidenceIds} />
      </article>
    ))}</div>
  );
}

function RunTargets({ r }: { r: MarketResult }) {
  if (!r.targets?.length) return <EmptyState title="Nenhuma empresa-alvo nesta execução">As empresas encontradas aparecem aqui e em Empresas-alvo, onde você aprova ou rejeita.</EmptyState>;
  return (
    <div className="card"><p className="card-sub">Empresas candidatas a cliente encontradas nesta execução. A curadoria (aprovar, rejeitar, acompanhar) é feita em <Link to="/empresas-alvo">Empresas-alvo</Link>; nada vira lead ou oportunidade.</p>
      <ul className="finding-list">{r.targets.map((t) => (
        <li key={t.id}><div><Link to={`/empresas-alvo/${t.id}`}><b>{t.name}</b></Link> <StatusPill tone={t.fitLevel === 'ALTA' ? 'mint' : t.fitLevel === 'MEDIA' ? 'amber' : 'gray'}>Aderência {FIT_TXT[t.fitLevel]}{t.fitScore !== null ? ` · ${t.fitScore}` : ''}</StatusPill> {t.hypothesis && <Nature n="HIPOTESE" />} {t.possibleDuplicate && <span className="tag">Possível duplicidade</span>}</div>
          <span className="cell-sub">{[t.domain, t.segment, [t.city, t.uf].filter(Boolean).join('/')].filter(Boolean).join(' · ')}</span>{t.rationale && <p>{t.rationale}</p>}</li>
      ))}</ul>
    </div>
  );
}
